import type { FormEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from '../components/AppShell';
import { EmptyState } from '../components/EmptyState';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Input,
  SectionHeader,
  Select,
  Skeleton,
  useActionDialog,
} from '../components/ui';
import {
  useCreatePointsReward,
  useDeactivatePointsReward,
  usePointsRewards,
  useUpdatePointsReward,
} from '../features/points/hooks/usePointsRewards';
import type { PointsReward, PointsRewardType } from '../types/admin';
import { parsePositiveSafeInteger } from '../utils/numbers';
import styles from './PointsRewardsPage.module.scss';

const REWARD_TYPES: readonly PointsRewardType[] = ['treatment_discount', 'item'];

export function PointsRewardsPage() {
  const { t } = useTranslation();
  const { confirmAction } = useActionDialog();

  const [showInactive, setShowInactive] = useState(false);
  const { data: rewards, isLoading, isError, error } = usePointsRewards(showInactive);
  const createMutation = useCreatePointsReward();
  const updateMutation = useUpdatePointsReward();
  const deactivateMutation = useDeactivatePointsReward();

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [cNameUk, setCNameUk] = useState('');
  const [cNameEn, setCNameEn] = useState('');
  const [cCost, setCCost] = useState('');
  const [cType, setCType] = useState<PointsRewardType>('treatment_discount');

  const [eNameUk, setENameUk] = useState('');
  const [eNameEn, setENameEn] = useState('');
  const [eCost, setECost] = useState('');
  const [eType, setEType] = useState<PointsRewardType>('treatment_discount');
  const [eActive, setEActive] = useState(true);

  function resetCreateForm() {
    setCNameUk('');
    setCNameEn('');
    setCCost('');
    setCType('treatment_discount');
  }

  function openCreate() {
    setEditingId(null);
    setShowCreate(true);
    resetCreateForm();
  }

  function openEdit(reward: PointsReward) {
    setShowCreate(false);
    setEditingId(reward.id);
    setENameUk(reward.name_uk);
    setENameEn(reward.name_en);
    setECost(String(reward.point_cost));
    setEType(reward.reward_type);
    setEActive(reward.is_active);
  }

  function closeForms() {
    setShowCreate(false);
    setEditingId(null);
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const cost = parsePositiveSafeInteger(cCost);
    if (cost === null) return;
    try {
      await createMutation.mutateAsync({
        name_uk: cNameUk.trim(),
        name_en: cNameEn.trim(),
        point_cost: cost,
        reward_type: cType,
      });
      setShowCreate(false);
      resetCreateForm();
    } catch {
      return;
    }
  }

  async function handleUpdate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editingId) return;
    const cost = parsePositiveSafeInteger(eCost);
    if (cost === null) return;
    try {
      await updateMutation.mutateAsync({
        id: editingId,
        payload: {
          name_uk: eNameUk.trim(),
          name_en: eNameEn.trim(),
          point_cost: cost,
          reward_type: eType,
          is_active: eActive,
        },
      });
      setEditingId(null);
    } catch {
      return;
    }
  }

  async function handleDeactivate(id: string) {
    const confirmed = await confirmAction({
      message: t('pointsRewards.confirmDeactivate'),
      confirmLabel: t('pointsRewards.deactivate'),
      tone: 'warning',
    });
    if (!confirmed) return;
    deactivateMutation.mutate(id);
  }

  const busy = createMutation.isPending || updateMutation.isPending || deactivateMutation.isPending;

  if (isLoading) {
    return (
      <AppShell>
        <div className={styles.page}>
          <SectionHeader title={t('pointsRewards.title')} subtitle={t('pointsRewards.subtitle')} />
          <div className={styles.skeletonList}>
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} height={72} />
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  if (isError) {
    return (
      <AppShell>
        <div className={styles.page}>
          <SectionHeader title={t('pointsRewards.title')} />
          <EmptyState
            title={t('pointsRewards.loadErrorTitle')}
            description={
              error instanceof Error ? error.message : t('pointsRewards.loadErrorDescription')
            }
          />
        </div>
      </AppShell>
    );
  }

  const list = rewards ?? [];

  return (
    <AppShell>
      <div className={styles.page}>
        <SectionHeader
          title={t('pointsRewards.title')}
          subtitle={t('pointsRewards.subtitle')}
          actions={
            <>
              <Button
                variant="secondary"
                onClick={() => setShowInactive((current) => !current)}
                disabled={busy}
              >
                {showInactive ? t('pointsRewards.hideInactive') : t('pointsRewards.showInactive')}
              </Button>
              <Button variant="primary" onClick={openCreate} disabled={busy}>
                {t('pointsRewards.addReward')}
              </Button>
            </>
          }
        />

        {showCreate ? (
          <Card padding="none">
            <CardHeader title={t('pointsRewards.newRewardTitle')} />
            <form onSubmit={handleCreate}>
              <CardBody>
                <div className={styles.formGrid}>
                  <Input
                    id="create-reward-name-uk"
                    label={t('pointsRewards.labelNameUk')}
                    value={cNameUk}
                    onChange={(e) => setCNameUk(e.target.value)}
                    placeholder={t('pointsRewards.placeholderNameUk')}
                  />
                  <Input
                    id="create-reward-name-en"
                    label={t('pointsRewards.labelNameEn')}
                    value={cNameEn}
                    onChange={(e) => setCNameEn(e.target.value)}
                    placeholder={t('pointsRewards.placeholderNameEn')}
                  />
                  <Input
                    type="number"
                    min={1}
                    id="create-reward-point-cost"
                    label={t('pointsRewards.labelPointCost')}
                    hint={t('pointsRewards.pointCostHint')}
                    value={cCost}
                    onChange={(e) => setCCost(e.target.value)}
                  />
                  <Select
                    id="create-reward-type"
                    label={t('pointsRewards.labelRewardType')}
                    value={cType}
                    onChange={(e) => setCType(e.target.value as PointsRewardType)}
                  >
                    {REWARD_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {t(`pointsRewards.rewardTypeLabels.${type}`)}
                      </option>
                    ))}
                  </Select>
                </div>
                {createMutation.error instanceof Error ? (
                  <p className={styles.inlineError}>{createMutation.error.message}</p>
                ) : null}
              </CardBody>
              <CardFooter>
                <Button variant="secondary" type="button" onClick={closeForms} disabled={busy}>
                  {t('pointsRewards.cancel')}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={
                    busy || !cNameUk.trim() || !cNameEn.trim() || parsePositiveSafeInteger(cCost) === null
                  }
                >
                  {createMutation.isPending ? t('pointsRewards.creating') : t('pointsRewards.create')}
                </Button>
              </CardFooter>
            </form>
          </Card>
        ) : null}

        {editingId ? (
          <Card padding="none">
            <CardHeader title={t('pointsRewards.editRewardTitle')} />
            <form onSubmit={handleUpdate}>
              <CardBody>
                <div className={styles.formGrid}>
                  <Input
                    id="edit-reward-name-uk"
                    label={t('pointsRewards.labelNameUk')}
                    value={eNameUk}
                    onChange={(e) => setENameUk(e.target.value)}
                  />
                  <Input
                    id="edit-reward-name-en"
                    label={t('pointsRewards.labelNameEn')}
                    value={eNameEn}
                    onChange={(e) => setENameEn(e.target.value)}
                  />
                  <Input
                    type="number"
                    min={1}
                    id="edit-reward-point-cost"
                    label={t('pointsRewards.labelPointCost')}
                    value={eCost}
                    onChange={(e) => setECost(e.target.value)}
                  />
                  <Select
                    id="edit-reward-type"
                    label={t('pointsRewards.labelRewardType')}
                    value={eType}
                    onChange={(e) => setEType(e.target.value as PointsRewardType)}
                  >
                    {REWARD_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {t(`pointsRewards.rewardTypeLabels.${type}`)}
                      </option>
                    ))}
                  </Select>
                  <label className={styles.checkboxField}>
                    <input
                      type="checkbox"
                      checked={eActive}
                      onChange={(e) => setEActive(e.target.checked)}
                    />
                    <span>{t('pointsRewards.labelActive')}</span>
                  </label>
                </div>
                {updateMutation.error instanceof Error ? (
                  <p className={styles.inlineError}>{updateMutation.error.message}</p>
                ) : null}
              </CardBody>
              <CardFooter>
                <Button variant="secondary" type="button" onClick={closeForms} disabled={busy}>
                  {t('pointsRewards.cancel')}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={
                    busy || !eNameUk.trim() || !eNameEn.trim() || parsePositiveSafeInteger(eCost) === null
                  }
                >
                  {updateMutation.isPending ? t('pointsRewards.saving') : t('pointsRewards.save')}
                </Button>
              </CardFooter>
            </form>
          </Card>
        ) : null}

        {list.length === 0 && !showCreate ? (
          <EmptyState
            title={t('pointsRewards.emptyTitle')}
            description={t('pointsRewards.emptyDescription')}
          />
        ) : list.length > 0 ? (
          <div className={styles.list}>
            {list.map((reward) => (
              <Card key={reward.id} padding="md">
                <div className={styles.row}>
                  <div className={styles.identity}>
                    <div className={styles.nameLine}>
                      <span className={styles.name}>{reward.name_uk}</span>
                      <Badge variant="neutral" size="sm">
                        {t('pointsRewards.costBadge', { count: reward.point_cost })}
                      </Badge>
                      {!reward.is_active ? (
                        <Badge variant="warning" size="sm">
                          {t('pointsRewards.inactiveBadge')}
                        </Badge>
                      ) : null}
                    </div>
                    <div className={styles.meta}>
                      <span className={styles.metaItem}>
                        <span className={styles.metaLabel}>{t('pointsRewards.colNameEn')}</span>
                        {reward.name_en}
                      </span>
                      <span className={styles.metaItem}>
                        <span className={styles.metaLabel}>{t('pointsRewards.colType')}</span>
                        {t(`pointsRewards.rewardTypeLabels.${reward.reward_type}`)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.rowActions}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(reward)}
                      disabled={busy || showCreate || (editingId !== null && editingId !== reward.id)}
                    >
                      {t('pointsRewards.edit')}
                    </Button>
                    {reward.is_active ? (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeactivate(reward.id)}
                        disabled={busy}
                      >
                        {t('pointsRewards.deactivate')}
                      </Button>
                    ) : null}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}
